import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, of, switchMap } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from '../../../core/api-service';
import { AiInsightsChatRequestActions, AiInsightsChatSessionActions, AiInsightsChatSuggestionsActions } from './ai-insights-actions';
import { TranslateService } from '@ngx-translate/core';
import { ChatMessageRole, ChatMessageType } from '../../../models/ai-insights/chat-message';
import { AiInsightsMessageParserService } from '../ai-insights-message-parser.service';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { selectChatSessionId, selectSessionDetailById, selectSessionPage, selectSessionPageSize, selectSessionsDict, selectSessionsPage, selectSessionsPageSize } from './ai-insights-selectors';
import { ChatExchange } from '../../../models/ai-insights/chat-exchange';
import { FeedbackService } from '../../../core/feedback.service';
import { v1 as generateUUID } from 'uuid';
import { getChatSessionId } from './ai-insights-state';

@Injectable()
export class AiInsightsEffects {
  constructor(private actions: Actions,
              private apiService: ApiService,
              private feedback: FeedbackService,
              private translate: TranslateService,
              private store: Store,
              private parser: AiInsightsMessageParserService) {
  }

  askAgent$ = createEffect(() => {
    return this.actions.pipe(
        ofType(AiInsightsChatRequestActions.requestChat),
        concatLatestFrom((action) => this.store.select(selectSessionDetailById(action.chatExchangeId))),
        switchMap(([action, chatExchange]) => {
          return this.apiService.askAgent(action.chatRequest).pipe(
              map((res) => {
                const newChatExchange: ChatExchange = {
                  ...chatExchange,
                  question: {
                    ...chatExchange.question,
                    id: res?.userMessage?.id,
                    timestamp: res?.userMessage?.timestamp,
                  },
                  answer: {
                    id: res?.agentMessage?.id,
                    timestamp: res?.agentMessage?.timestamp,
                    body: this.parser.parseChatMessage(res?.agentMessage?.text, res?.agentMessage?.role),
                  },
                };

                return AiInsightsChatRequestActions.requestChatSuccess({ chatExchange: newChatExchange });
              }),
              catchError((err: HttpErrorResponse) => {
                console.error(err.error);
                const newChatExchange: ChatExchange = {
                  ...chatExchange,
                  answer: {
                    body: [
                      {
                        type: ChatMessageType.TEXT,
                        content: this.translate.instant('ERROR'),
                        role: ChatMessageRole.SYSTEM,
                      },
                    ],
                  },
                };

                return of(AiInsightsChatRequestActions.requestChatError({
                  chatExchange: newChatExchange,
                }));
              }),
          );
        }),
    );
  });

  getChatSuggestions$ = createEffect(() => {
    return this.actions.pipe(
        ofType(AiInsightsChatSuggestionsActions.getSuggestions),
        switchMap((action) => {
          return this.apiService.getChatSuggestions(action.lang).pipe(
              map((res) => {
                return AiInsightsChatSuggestionsActions.getSuggestionsSuccess({ chatSuggestions: res });
              }),
              catchError((err: HttpErrorResponse) => {
                this.handleError(err, this.translate.instant('AI_INSIGHTS.ERROR_PROMPTS'));
                return of(AiInsightsChatSuggestionsActions.getSuggestionsError());
              }),
          );
        }),
    );
  });

  getChatSessions$ = createEffect(() => {
    return this.actions.pipe(
        ofType(AiInsightsChatSessionActions.getChatSessions),
        concatLatestFrom(() => [
          this.store.select(selectSessionsPage),
          this.store.select(selectSessionsPageSize),
        ]),
        switchMap(([action, currentPage, pageSize]) => {
          const _page = action.page || currentPage || 1;
          return this.apiService.getChatSessions(_page, pageSize).pipe(
              map((res) => {
                return AiInsightsChatSessionActions.getChatSessionsSuccess({ sessions: res });
              }),
              catchError((err) => {
                // TODO: maybe add something more specific
                this.handleError(err, this.translate.instant('ERROR'));
                return of(AiInsightsChatSessionActions.getChatSessionsError());
              }),
          );
        }),
    );
  });

  getChatSession$ = createEffect(() => {
    return this.actions.pipe(
        ofType(AiInsightsChatSessionActions.getChatSessionDetail,
            AiInsightsChatSessionActions.selectExistingChatSession),
        concatLatestFrom((_) => [
          this.store.select(selectSessionPage),
          this.store.select(selectSessionPageSize),
        ]),
        switchMap(([action, currentPage, pageSize]) => {
          const _page = action.type === AiInsightsChatSessionActions.getChatSessionDetail.type &&
              action.page ||
              currentPage ||
              1;

          return this.apiService.getChatSessionDetail(action.sessionId, _page, pageSize).pipe(
              map((res) => {
                const sessionDetail = res?.conversation.map((agentResponse) => {
                  const newChatExchange: ChatExchange = {
                    id: generateUUID(),
                    question: {
                      id: agentResponse?.userMessage?.id,
                      timestamp: agentResponse?.userMessage?.timestamp,
                      body: {
                        type: ChatMessageType.TEXT,
                        content: agentResponse?.userMessage?.text,
                        role: agentResponse?.userMessage?.role,
                      },
                    },
                    answer: {
                      id: agentResponse?.agentMessage?.id,
                      timestamp: agentResponse?.agentMessage?.timestamp,
                      body: this.parser.parseChatMessage(agentResponse?.agentMessage?.text,
                          agentResponse?.agentMessage?.role),
                    },
                  };

                  return newChatExchange;
                });

                return AiInsightsChatSessionActions.getChatSessionDetailSuccess({
                  sessionDetail: sessionDetail,
                  page: res?.page,
                  totalPages: res?.total_pages,
                });
              }),
              catchError((err: HttpErrorResponse) => {
                // TODO: maybe add something more specific
                this.handleError(err, this.translate.instant('ERROR'));
                return of(AiInsightsChatSessionActions.getChatSessionDetailError());
              }),
          );
        }),
    );
  });


  refreshSessionHistory$ = createEffect(() => {
    return this.actions.pipe(
        ofType(
            // Make the respective session button appear in the session history after 1st agent answer
            AiInsightsChatRequestActions.requestChatSuccess,
            // Make the respective session button disappear after user deletes the sessions
            AiInsightsChatSessionActions.deleteChatSessionSuccess),
        concatLatestFrom((_) => [
          this.store.select(selectChatSessionId),
          this.store.select(selectSessionsDict),
        ]),
        filter(([action, id, sessionsDict]) =>
            AiInsightsChatRequestActions.requestChatSuccess.type === action.type ?
                // skip if we have the current session in session history
                !sessionsDict?.[id] :
                true),
        map(() => AiInsightsChatSessionActions.getChatSessions({ page: 1 })),
    );
  });

  renameChatSession$ = createEffect(() => {
    return this.actions.pipe(
        ofType(AiInsightsChatSessionActions.renameChatSession),
        switchMap(({ sessionId, sessionTitle }) => {
          return this.apiService.renameChatSession(sessionId, sessionTitle).pipe(
              map((res) => AiInsightsChatSessionActions.renameChatSessionSuccess({ session: res })),
              catchError((err: HttpErrorResponse) => {
                // TODO: maybe add something more specific
                this.handleError(err, this.translate.instant('ERROR'));
                return of(AiInsightsChatSessionActions.renameChatSessionFail());
              }),
          );
        }),
    );
  });

  deleteChatSession$ = createEffect(() => {
    return this.actions.pipe(
        ofType(AiInsightsChatSessionActions.deleteChatSession),
        concatLatestFrom(() => this.store.select(selectChatSessionId)),
        switchMap(([action, activeSessionId]) => {
          return this.apiService.deleteChatSession(action.sessionId).pipe(
              mergeMap(() => {
                const actions: Action<any>[] = [
                  AiInsightsChatSessionActions.deleteChatSessionSuccess({ sessionId: action.sessionId }),
                ];
                // reset active sessionID and conversation
                if (action.sessionId === activeSessionId) {
                  actions.push(AiInsightsChatSessionActions.startNewChatSession({
                    sessionId: getChatSessionId(),
                  }));
                }

                return actions;
              }),
              catchError((err: HttpErrorResponse) => {
                // TODO: maybe add something more specific
                this.handleError(err, this.translate.instant('ERROR'));
                return of(AiInsightsChatSessionActions.deleteChatSessionFail());
              }),
          );
        }),
    );
  });


  private handleError(err: HttpErrorResponse, userFriendlyMessage: string) {
    console.error(err.error);
    this.feedback.showAlertSnackbar({
      data: {
        userFriendlyMessage: userFriendlyMessage,
      },
    }, err.status ? err.status : err.error.status);
  }

}
